import { useState } from "react";
import {
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Table,
  Tag,
  Space,
} from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  DeploymentUnitOutlined,
  InboxOutlined,
  ExportOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import moment from "moment";

import MyPage from "../../../../../components/pages/Page";
import { useFetch } from "../../../../../hooks/useFetch";
import "../table-compact.css";

const { Option } = Select;
const { RangePicker } = DatePicker;

const toneToColor = (tone) => {
  const t = String(tone || "").toLowerCase();
  if (t === "green" || t === "success") return "success";
  if (t === "red" || t === "error" || t === "danger") return "error";
  if (t === "yellow" || t === "orange" || t === "warning") return "warning";
  if (t === "blue" || t === "processing" || t === "info") return "processing";
  return "default";
};

export default () => {
  // --- Card summary: live dari API ---
  // GET /api/v1/beacukai/report/card?start_date=YYYYMMDD&end_date=YYYYMMDD
  // respons (payload.data): { start_date, end_date,
  //   data: [{ label, value, unit?, text? }], detail: {...} }
  // label: demolition | hoard | spending | clearance_time
  const [startDate, setStartDate] = useState(() =>
    moment("2026-10-01", "YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(() =>
    moment("2026-11-04", "YYYY-MM-DD")
  );
  const cardQuery = `api/v1/beacukai/report/card?start_date=${startDate.format(
    "YYYYMMDD"
  )}&end_date=${endDate.format("YYYYMMDD")}`;
  const [cardResp, cardLoading] = useFetch(cardQuery);

  const cardList = Array.isArray(cardResp?.data) ? cardResp.data : [];
  const byLabel = (label) => cardList.find((d) => d?.label === label) || {};
  const demolitionVal = Number(byLabel("demolition")?.value) || 0;
  const hoardVal = Number(byLabel("hoard")?.value) || 0;
  const spendingVal = Number(byLabel("spending")?.value) || 0;
  const clearanceItem = byLabel("clearance_time");
  const clearanceText =
    clearanceItem?.text ||
    (clearanceItem?.value != null ? `${clearanceItem.value} Jam` : "-");

  // --- Ringkasan laporan: live dari API, month dari filter Bulan & Tahun ---
  // GET /api/v1/beacukai/report/summary?month=YYYYMM
  // respons (payload.data): { start_date, end_date, periode,
  //   data: [{ no, key, menu, periode, masuk_text, keluar_text,
  //   selisih_text, status: { label, tone } }] }
  const [filterMonth, setFilterMonth] = useState(() =>
    moment("2026-10-01", "YYYY-MM-DD")
  );
  const summaryQuery = `api/v1/beacukai/report/summary?month=${filterMonth.format(
    "YYYYMM"
  )}`;
  const [summaryResp, summaryLoading] = useFetch(summaryQuery);

  const summaryRows = Array.isArray(summaryResp?.data)
    ? summaryResp.data
    : [];
  const summaryPeriode =
    summaryResp?.periode || filterMonth.format("MMM YYYY");
  const tableData = summaryRows.map((r, i) => ({
    key: r.key || r.no || i + 1,
    ...r,
  }));

  const renderMultiline = (text) => {
    if (!text || text === "-") return "-";
    return (
      <div style={{ whiteSpace: "pre-line" }}>
        {String(text)
          .split("\n")
          .map((line, i) => (
            <div key={i}>{line}</div>
          ))}
      </div>
    );
  };

  const columns = [
    {
      title: "No",
      dataIndex: "no",
      key: "no",
      width: 50,
      align: "center",
    },
    {
      title: "Jenis Sub-Menu Laporan",
      dataIndex: "menu",
      key: "menu",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Periode",
      dataIndex: "periode",
      key: "periode",
      align: "center",
    },
    {
      title: "Total Masuk (Manifest / Gate In)",
      dataIndex: "masuk_text",
      key: "masuk_text",
      render: renderMultiline,
    },
    {
      title: "Total Keluar / Timbun",
      dataIndex: "keluar_text",
      key: "keluar_text",
      render: renderMultiline,
    },
    {
      title: "Selisih / Anomali",
      dataIndex: "selisih_text",
      key: "selisih_text",
    },
    {
      title: "Status Indikator",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const label =
          typeof status === "object" ? status?.label : status;
        const tone = typeof status === "object" ? status?.tone : null;
        if (!label) return "-";
        // legacy string fallback
        if (!tone) {
          let color = "default";
          if (label.includes("Required")) color = "warning";
          if (label.includes("Needed")) color = "error";
          if (label.includes("Normal") || label.includes("Target"))
            color = "success";
          return (
            <Tag color={color} style={{ borderRadius: "10px" }}>
              ● {label}
            </Tag>
          );
        }
        return (
          <Tag color={toneToColor(tone)} style={{ borderRadius: "10px" }}>
            ● {label}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: () => (
        <Button type="link" size="small">
          [Lihat Detail]
        </Button>
      ),
    },
  ];

  return (
    <MyPage title={"Laporan"}>
      <div
        className="dashboard-filters"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "20px",
          paddingBottom: "4px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "13px", color: "#1e293b", fontWeight: 500 }}>
            Filter Tampilan:
          </span>

          <Select
            defaultValue="harian"
            style={{ width: 100 }}
            size="small"
            options={[
              { value: "harian", label: "Harian" },
              { value: "mingguan", label: "Mingguan" },
            ]}
          />

          <Select
            defaultValue="bulanan"
            style={{ width: 105 }}
            size="small"
            options={[
              { value: "bulanan", label: "Bulanan" },
              { value: "tahunan", label: "Tahunan" },
            ]}
          />

          <RangePicker
            size="small"
            style={{ width: 300 }}
            prefixIcon={
              <CalendarOutlined
                style={{ color: "#64748b", fontSize: "12px" }}
              />
            }
            suffixIcon={
              <CalendarOutlined
                style={{ color: "#64748b", fontSize: "12px" }}
              />
            }
            separator=" - "
            value={[startDate, endDate]}
            onChange={(dates) => {
              if (dates?.[0]) setStartDate(dates[0].clone());
              if (dates?.[1]) setEndDate(dates[1].clone());
            }}
            format="DD MMMM YYYY"
          />
        </div>

        <div style={{ fontSize: "12px", color: "#475569" }}>
          Terakhir Diperbarui:{" "}
          <span style={{ fontWeight: 500 }}>
            {endDate.format("DD MMMM YYYY, HH:mm")} WIB
            {cardLoading ? " (memuat...)" : ""}
          </span>
        </div>
      </div>

      <Row gutter={12} style={{ marginBottom: "24px" }}>
        <Col span={6}>
          <Card
            loading={cardLoading}
            style={{
              background: "#e6f7ff",
              border: "1px solid #91d5ff",
              borderRadius: "6px",
            }}
            bodyStyle={{ padding: "12px" }}
          >
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <DeploymentUnitOutlined
                style={{
                  fontSize: "24px",
                  marginRight: "8px",
                  color: "#0050b3",
                }}
              />
              <div>
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "#0050b3",
                  }}
                >
                  1. Laporan Pembongkaran
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    marginTop: "4px",
                    fontWeight: "500",
                  }}
                >
                  Selisih Manifest & Gate In
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#595959",
                    marginTop: "2px",
                  }}
                >
                  Variance:{" "}
                  {cardLoading ? "..." : `${demolitionVal} HAWB`}
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col span={6}>
          <Card
            loading={cardLoading}
            style={{
              background: "#fff7e6",
              border: "1px solid #ffd591",
              borderRadius: "6px",
            }}
            bodyStyle={{ padding: "12px" }}
          >
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <InboxOutlined
                style={{
                  fontSize: "24px",
                  marginRight: "8px",
                  color: "#c45100",
                }}
              />
              <div>
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "#c45100",
                  }}
                >
                  2. Laporan Daftar Timbun
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    marginTop: "4px",
                    fontWeight: "500",
                  }}
                >
                  Data Timbun (Gate In - Gate Out, Bulanan)
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#595959",
                    marginTop: "2px",
                  }}
                >
                  Stock: {cardLoading ? "..." : `${hoardVal} HAWB`}{" "}
                  {!cardLoading && hoardVal > 0 && (
                    <span
                      style={{
                        fontSize: "10px",
                        background: "#ffccc7",
                        padding: "1px 4px",
                        borderRadius: "3px",
                      }}
                    >
                      Termasuk Barang Abandon
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col span={6}>
          <Card
            loading={cardLoading}
            style={{
              background: "#f6ffed",
              border: "1px solid #b7eb8f",
              borderRadius: "6px",
            }}
            bodyStyle={{ padding: "12px" }}
          >
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <ExportOutlined
                style={{
                  fontSize: "24px",
                  marginRight: "8px",
                  color: "#237804",
                }}
              />
              <div>
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "#237804",
                  }}
                >
                  3. Laporan Pengeluaran
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    marginTop: "4px",
                    fontWeight: "500",
                  }}
                >
                  Selisih Gate In & Gate Out (Bulanan)
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#595959",
                    marginTop: "2px",
                  }}
                >
                  Gate Out:{" "}
                  {cardLoading ? "..." : `${spendingVal} HAWB`}
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col span={6}>
          <Card
            loading={cardLoading}
            style={{
              background: "#e6fffb",
              border: "1px solid #87e8de",
              borderRadius: "6px",
            }}
            bodyStyle={{ padding: "12px" }}
          >
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <ClockCircleOutlined
                style={{
                  fontSize: "24px",
                  marginRight: "8px",
                  color: "#006d75",
                }}
              />
              <div>
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "#006d75",
                  }}
                >
                  4. Laporan Import Clearance Time
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    marginTop: "4px",
                    fontWeight: "500",
                  }}
                >
                  Bongkar sampai Pengeluaran Gudang
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#595959",
                    marginTop: "2px",
                  }}
                >
                  Rata-rata: {cardLoading ? "..." : clearanceText}
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card
        style={{ marginBottom: "24px", borderRadius: "6px" }}
        bodyStyle={{ padding: "12px 16px" }}
      >
        <div
          style={{ marginBottom: "8px", fontWeight: "bold", fontSize: "13px" }}
        >
          🔍 Filter Laporan & Ekspor Data
        </div>
        <Row gutter={4} align="middle">
          <Col span={4}>
            <Select
              style={{ width: 170 }}
              placeholder="Pilih Laporan"
              size="small"
            >
              <Option value="1">Laporan Pembongkaran</Option>
              <Option value="2">Laporan Daftar Timbun</Option>
              <Option value="3">Laporan Pengeluaran</Option>
              <Option value="4">Import Clearance Time</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Space size={4}>
              <span style={{ fontSize: "12px" }}>Periodik:</span>
              <Select defaultValue="Bulanan" size="small" style={{ width: 90 }}>
                <Option value="Bulanan">Bulanan</Option>
                <Option value="Tahunan">Tahunan</Option>
              </Select>
            </Space>
          </Col>
          <Col span={4}>
            <Space size={4}>
              <span style={{ fontSize: "12px" }}>Bulan & Tahun:</span>
              <DatePicker
                picker="month"
                size="small"
                style={{ width: 150 }}
                value={filterMonth}
                onChange={(d) => {
                  if (d) setFilterMonth(d.clone().startOf("month"));
                }}
                format="MMMM YYYY"
              />
            </Space>
          </Col>
          <Col span={4}>
            <Space size={4}>
              <span style={{ fontSize: "12px" }}>Format Unduh:</span>
              <Select defaultValue="excel" size="small" style={{ width: 120 }}>
                <Option value="excel">Excel (.xlsx)</Option>
                <Option value="pdf">PDF (.pdf)</Option>
              </Select>
            </Space>
          </Col>
          <Col span={8} style={{ textAlign: "right" }}>
            <Space size="small">
              {" "}
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                size="small"
                style={{ backgroundColor: "#141414", borderColor: "#141414" }}
              >
                Unduh Laporan
              </Button>
              <Button
                icon={<EyeOutlined />}
                size="small"
                style={{
                  backgroundColor: "#141414",
                  color: "#fff",
                  borderColor: "#141414",
                }}
              >
                Pratinjau Data
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <div
        style={{
          marginBottom: "8px",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          fontSize: "13px",
        }}
      >
        <span style={{ marginRight: "6px" }}>📋</span> Ringkasan & Pratinjau
        Data Laporan Utama
      </div>

      <Table
        className="bv2-compact"
        title={() => (
          <div style={{ fontWeight: "bold", padding: "2px 4px" }}>
            Ringkasan Laporan Bulan Ini ({summaryPeriode})
            {summaryLoading ? " (memuat...)" : ""}
          </div>
        )}
        dataSource={tableData}
        loading={summaryLoading}
        columns={columns}
        bordered
        pagination={{ pageSize: 5 }}
        size="small"
      />
    </MyPage>
  );
};
